import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Calendar,
  CalendarCheck, CalendarDays, Tag, Settings, LogOut, Menu, Zap, HelpCircle, Contact,
  Clock, CheckCircle2, MessageCircle, AlertCircle, Users, Megaphone, Lock, Wallet
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { auth } from '../config/firebase';
import { usuarioService, pagamentoService } from '../services/api';
import { temAcesso as podeAcessar, getLimite as obterLimite, TRIAL_PADRAO } from '../config/planos';

import MinhaAgendaPage from './MinhaAgendaPage';
import VisualizarConsultas from './VisualizarConsultas';
import CatalogoServicos from '../components/CatalogoServicos';
import ClientesPage from './ClientesPage';
import MarketingPage from './MarketingPage';
import FinanceiroPage from './FinanceiroPage';
import AutomacaoPage from './AutomacaoPage';
import PlanosPage from './PlanosPage';
import ResumoDashboard from './ResumoDashboard';
import AjudaPage from './AjudaPage';
import OnboardingWizard from './OnboardingWizard';
import GerenciarEquipe from '../components/GerenciarEquipe';

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeItem, setActiveItem] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingCompleto, setOnboardingCompleto] = useState(true); // true até carregar, pra não piscar o aviso à toa
  
  const [medico, setMedico] = useState({ 
    nome: 'Carregando...', 
    iniciais: '..',
    status_assinatura: 'FREE', 
    dias_restantes: 0
  });

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error("Erro ao deslogar:", error);
    }
  };

  const currentUser = auth.currentUser;
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  useEffect(() => {
    const carregarDados = async () => {
      try {
        if (currentUser) {
          const token = await currentUser.getIdToken();
          const usuarioId = currentUser.uid;

          const queryParams = new URLSearchParams(location.search);
          if (queryParams.get('status') === 'google_success') {
            try {
              const payloadTrial = {
                diasTrial: TRIAL_PADRAO.dias,
                nome: currentUser.displayName || "Usuario",
                email: currentUser.email,
                usuarioId: usuarioId
              };
              await pagamentoService.ativarTrial(payloadTrial, token);
              navigate('/dashboard', { replace: true });
            } catch (trialErr) {
              console.error("Erro na activation do trial:", trialErr);
            }
          }

          const resOnboarding = await usuarioService.verificarOnboarding(usuarioId);
          setOnboardingCompleto(resOnboarding.data.completo);
          // O usuário pode dispensar a tela de boas-vindas e conectar WhatsApp/Google depois,
          // direto pela aba Automação — sem esse flag ela voltaria a bloquear a tela a cada
          // recarregamento enquanto os dois passos não estivessem completos.
          const dispensado = localStorage.getItem(`onboarding_dispensado_${usuarioId}`) === 'true';
          setShowOnboarding(!resOnboarding.data.completo && !dispensado);

          const responsePerfil = await usuarioService.obterPerfil(usuarioId);
          const dataPerfil = responsePerfil.data;
          
          const nomeCompleto = dataPerfil.nome || "Usuário";
          const iniciais = nomeCompleto.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
          
          const responseTrial = await pagamentoService.verificarTrial(usuarioId, token);
          const dataTrial = responseTrial.data; 

          let dias = 0;
          if (dataTrial.data_validade) {
            const hoje = new Date();
            const validade = new Date(dataTrial.data_validade);
            const diffTime = validade - hoje;
            dias = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          }

          setMedico({
            nome: nomeCompleto,
            iniciais,
            status_assinatura: dataTrial.status_assinatura || 'FREE',
            dias_restantes: dias > 0 ? dias : 0,
            plano: dataPerfil.plano || 'COMUM', // <--- ADICIONE ISSO
            slug: dataPerfil.slug || ''
          });
        }
      } catch (error) {
        console.error("Erro ao carregar perfil ou status do plano:", error);
      }
    };
    carregarDados();
  }, [currentUser, navigate, location.search]);

  const isPremium = medico.status_assinatura === 'ACTIVE' ||
                    (medico.status_assinatura === 'TRIAL' && medico.dias_restantes > 0);

  // Entitlements centralizados em ../config/planos.js: `temAcesso`/`getLimite` já resolvem o
  // plano efetivo (Grátis quando não pago) e o rank mínimo de cada feature. Adicionar/remover
  // um plano ou mudar o que cada nível libera é uma mudança só naquele arquivo, não aqui.
  const temAcesso = (feature) => podeAcessar(medico.plano, isPremium, feature);
  const getLimite = (chave) => obterLimite(medico.plano, isPremium, chave);

  // Todo recurso fica sempre visível no menu — o bloqueio é comunicado dentro de cada página
  // (cadeado + PaywallCard), nunca escondendo o item ou redirecionando o usuário.
  const menuItems = [
    { id: 'dashboard', label: 'Visão Geral', icon: <LayoutDashboard size={20} /> },
    { id: 'meusAgendamentos', label: 'Meus Agendamentos', icon: <CalendarDays size={20} /> },
    { id: 'minhaAgenda', label: 'Agenda', icon: <CalendarCheck size={20} /> },
    { id: 'servicos', label: 'Serviços', icon: <Tag size={20} /> },
    { id: 'clientes', label: 'Clientes', icon: <Contact size={20} /> },
    { id: 'financeiro', label: 'Financeiro', icon: <Wallet size={20} />, feature: 'financeiro' },
    { id: 'marketing', label: 'Marketing', icon: <Megaphone size={20} />, feature: 'marketing' },
    { id: 'equipe', label: 'Gerenciar Equipe', icon: <Users size={20} />, feature: 'gerenciar_equipe' },
    { id: 'planos', label: 'Planos', icon: <Calendar size={20} /> },
    { id: 'automacao', label: 'Automação', icon: <Settings size={20} /> },
    { id: 'help', label: 'Ajuda e Tutorial', icon: <HelpCircle size={20} /> },
  ].map(item => ({
    ...item,
    locked: item.feature ? !temAcesso(item.feature) : false
  }));

  const Logo = () => (
    <div className="flex items-center justify-start">
      <img 
        src="/logo-sincro1.png" 
        alt="Agenda Sincro Logo" 
        className="h-10 w-auto object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
      />
    </div>
  );

  const Logo1 = () => (
    <div className="flex items-center justify-start">
      <img 
        src="/logo-sutil.png" 
        alt="Agenda Sincro Logo" 
        className="h-10 w-auto object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
      />
    </div>
  );

  const renderContent = () => {
    const components = {
      dashboard: <ResumoDashboard setActiveItem={setActiveItem} temAcesso={temAcesso} slug={medico.slug} />,
      minhaAgenda: <MinhaAgendaPage temAcesso={temAcesso} />,
      meusAgendamentos: <VisualizarConsultas isPremium={isPremium} getLimite={getLimite} />,
      servicos: <CatalogoServicos temAcesso={temAcesso} />,
      clientes: <ClientesPage temAcesso={temAcesso} />,
      financeiro: <FinanceiroPage temAcesso={temAcesso} />,
      marketing: <MarketingPage temAcesso={temAcesso} />,
      planos: <PlanosPage />,
      automacao: <AutomacaoPage temAcesso={temAcesso} getLimite={getLimite} />,
      help: <AjudaPage />,
      equipe: <GerenciarEquipe temAcesso={temAcesso} />,
    };
    return components[activeItem] || <ResumoDashboard />;
  };

  return (
    <div className="flex h-screen bg-[#F8F7FF] font-sans overflow-hidden">
      {showOnboarding && (
        <OnboardingWizard
          onComplete={() => setShowOnboarding(false)}
          onSkip={() => {
            localStorage.setItem(`onboarding_dispensado_${currentUser.uid}`, 'true');
            setShowOnboarding(false);
            setActiveItem('automacao');
          }}
        />
      )}

      {/* Camada de fundo opaca ao abrir o menu mobile */}
      {isSidebarOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[1050] lg:hidden" onClick={toggleSidebar} />
      )}

      {/* ASIDE / SIDEBAR BARRA LATERAL */}
      <aside className={`fixed lg:static top-0 left-0 h-full w-[280px] bg-[#0f172a] text-white flex flex-col py-8 transition-transform duration-400 ease-in-out z-[1100] lg:transform-none ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="px-8 pb-10">
          <Link to="/" className="no-underline">
            <Logo />
          </Link>
        </div>

        <nav className="flex-1 px-4 overflow-y-auto">
          {menuItems.map((item) => (
            <div
              key={item.id}
              onClick={() => { setActiveItem(item.id); setIsSidebarOpen(false); }}
              className={`flex items-center gap-3 px-5 py-3.5 cursor-pointer rounded-2xl text-xs font-bold uppercase tracking-wide transition-all duration-300 mb-1 ${activeItem === item.id ? 'text-white bg-gradient-to-r from-[#1883FF] to-[#6D28D9] shadow-[0_10px_20px_rgba(109,40,217,0.2)]' : 'text-[#94a3b8] hover:bg-white/5 hover:text-white'}`}
            >
              {item.icon}
              <span className="flex-1">{item.label}</span>
              {item.locked && <Lock size={12} className="opacity-70" />}
            </div>
          ))}
        </nav>

        <div onClick={handleLogout} className="px-8 py-5 cursor-pointer text-[#f87171] font-extrabold text-xs flex items-center gap-2.5 uppercase mt-auto hover:opacity-80 transition-opacity">
          <LogOut size={18} /> Sair
        </div>
      </aside>

      {/* ÁREA DE CONTEÚDO PRINCIPAL */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        
        {/* BARRA DE NAVEGAÇÃO SUPERIOR */}
        <header className="h-20 bg-white/80 backdrop-blur-md border-b border-purple-100 flex items-center justify-between px-6 md:px-10 sticky top-0 z-50">
          <div className="flex items-center gap-4">
            <button className="lg:hidden bg-slate-100 border-none p-2.5 rounded-xl cursor-pointer text-[#64748b] hover:bg-slate-200 transition-colors" onClick={toggleSidebar}>
              <Menu size={24} />
            </button>
            <div className="lg:hidden">
              <Logo1 />
            </div>

            <div className="hidden md:flex ml-5">
              {medico.status_assinatura === 'TRIAL' && medico.dias_restantes > 0 ? (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider bg-orange-50 text-orange-700 border border-orange-100 animate-pulse">
                  <Zap size={12} fill="currentColor" />
                  Trial Pro: {medico.dias_restantes} dias
                </div>
              ) : medico.status_assinatura === 'ACTIVE' ? (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider bg-green-50 text-green-700 border border-green-100">
                  <CheckCircle2 size={12} />
                  Plano Pro Ativo
                </div>
              ) : medico.status_assinatura === 'TRIAL' ? (
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider bg-red-50 text-red-500 border border-red-200 cursor-pointer transition-colors hover:bg-red-100"
                  onClick={() => setActiveItem('planos')}
                >
                  <AlertCircle size={12} />
                  Trial Expirado - Ativar Pro
                </div>
              ) : (
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-500 border border-slate-200 cursor-pointer transition-colors hover:bg-slate-200"
                  onClick={() => setActiveItem('planos')}
                >
                  <Zap size={12} />
                  Conta Gratuita
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-[10px] font-black text-[#94a3b8] tracking-widest uppercase mb-0.5">CONECTADO</p>
              <p className="text-sm font-extrabold text-[#1e293b] leading-tight">{medico.nome}</p>
              
              {medico.status_assinatura === 'TRIAL' && medico.dias_restantes > 0 && (
                <span className="text-[9px] font-bold text-[#94a3b8] flex items-center justify-end gap-1 mt-0.5">
                  <Clock size={10} /> Expira em {medico.dias_restantes} dias
                </span>
              )}
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-[#1883FF] to-[#6D28D9] rounded-xl flex items-center justify-center text-white font-black text-xs border-2 border-white shadow-[0_4px_10px_rgba(109,40,217,0.15)]">
              {medico.iniciais}
            </div>
          </div>
        </header>

        {/* WORKSPACE DE COMPONENTES INTERNOS */}
        <div className="flex-1 p-6 md:p-10 overflow-y-auto pb-28 lg:pb-10">
          <h1 className="text-2xl font-black text-[#1e293b] uppercase italic tracking-tight mb-8">
            {menuItems.find(i => i.id === activeItem)?.label || 'Visão Geral'}
          </h1>

          {!onboardingCompleto && activeItem !== 'automacao' && (
            <div
              className="flex items-center justify-between gap-4 bg-orange-50 border border-orange-100 rounded-2xl px-6 py-4 mb-6 cursor-pointer hover:bg-orange-100/70 transition-colors"
              onClick={() => setActiveItem('automacao')}
            >
              <div className="flex items-center gap-3">
                <AlertCircle size={18} className="text-orange-600 shrink-0" />
                <span className="text-xs sm:text-sm font-bold text-orange-800">
                  Falta conectar seu WhatsApp e/ou Google Agenda pra o robô começar a atender de verdade.
                </span>
              </div>
              <span className="text-[10px] font-black uppercase tracking-wider text-orange-700 whitespace-nowrap">Conectar agora →</span>
            </div>
          )}

          <div className="bg-white rounded-[2.5rem] p-6 md:p-10 border border-purple-50 shadow-[0_10px_30px_rgba(109,40,217,0.03)]">
            {renderContent()}
          </div>
        </div>

        {/* BOTÃO FLUTUANTE DE SUPORTE WHATSAPP */}
        <a 
          href="https://wa.me/558521807757?text=Olá! Preciso de suporte com a Agenda Sincro." 
          target="_blank" 
          rel="noreferrer" 
          className="fixed bottom-[90px] md:bottom-[30px] right-5 md:right-[30px] bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white p-3 md:px-6 md:py-3 rounded-full md:rounded-full flex items-center gap-2.5 text-xs font-black uppercase tracking-wider shadow-[0_10px_25px_rgba(109,40,217,0.3)] transition-all duration-300 hover:-translate-y-1 z-50"
        >
          <MessageCircle size={20} />
          <span className="hidden md:inline">Suporte VIP</span>
        </a>

        {/* BARRA DE NAVEGAÇÃO INFERIOR MOBILE */}
        <nav className="lg:hidden fixed bottom-0 left-0 w-full h-[75px] bg-white/95 backdrop-blur-xl border-t border-purple-50 flex justify-around items-center z-[1000]">
          <div className={`flex flex-col items-center gap-1 text-[9px] font-black uppercase tracking-wider cursor-pointer transition-colors ${activeItem === 'dashboard' ? 'text-[#6D28D9]' : 'text-[#94a3b8]'}`} onClick={() => setActiveItem('dashboard')}>
            <LayoutDashboard size={22} />
            <span>Início</span>
          </div>

          <div className={`flex flex-col items-center gap-1 text-[9px] font-black uppercase tracking-wider cursor-pointer transition-colors ${activeItem === 'minhaAgenda' ? 'text-[#6D28D9]' : 'text-[#94a3b8]'}`} onClick={() => setActiveItem('minhaAgenda')}>
            <CalendarCheck size={22} />
            <span>Agenda</span>
          </div>

          {/* Botão de Destaque central (FAB) */}
          <div className="w-[56px] h-[56px] bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white rounded-full flex items-center justify-center -mt-9 border-[4px] border-white shadow-[0_10px_25px_rgba(109,40,217,0.4)] cursor-pointer transition-all active:scale-95" onClick={() => setActiveItem(isPremium ? 'automacao' : 'planos')}>
            {isPremium ? <Zap size={26} fill="currentColor" /> : <Calendar size={24} />}
          </div>

          <div className="flex flex-col items-center gap-1 text-[9px] font-black uppercase tracking-wider cursor-pointer text-[#94a3b8]" onClick={() => setActiveItem('planos')}>
            <Calendar size={22} />
            <span>Planos</span>
          </div>

          <div className="flex flex-col items-center gap-1 text-[9px] font-black uppercase tracking-wider cursor-pointer text-[#94a3b8]" onClick={toggleSidebar}>
            <Menu size={22} />
            <span>Menu</span>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default DashboardPage;